/**
 * DirectMessages — Private Communications Hub
 * Co-parent, Attorney, Medical (HIPAA), Admin channels
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import { data as api } from '@/utils/auth'
import { useApp } from '@/context/AppContext'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
// Note: keyboard height avoidance is handled globally in App.jsx (shrinks app shell).
// The listener here only handles scroll-to-bottom when the keyboard opens.

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diffDays = Math.floor((now - d) / 86400000)
  if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7)  return d.toLocaleDateString('en-US', { weekday: 'short' })
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function fmtFull(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

const TYPE_META = {
  coparent: { label: 'Co-Parent',  emoji: '👨‍👩‍👧', color: 'bg-blue-100 text-blue-700',   ring: 'border-blue-300' },
  attorney: { label: 'Attorney',   emoji: '⚖️',    color: 'bg-yellow-100 text-yellow-700', ring: 'border-yellow-300' },
  medical:  { label: 'Medical',    emoji: '🏥',    color: 'bg-pink-100 text-pink-700',    ring: 'border-pink-300' },
  admin:    { label: 'Admin',      emoji: '🔔',    color: 'bg-slate-100 text-slate-600',  ring: 'border-slate-300' },
}

// ── HIPAA Consent Modal ───────────────────────────────────────────────────────
function HipaaModal({ onAccept, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onCancel}>
      <div className="w-full bg-white rounded-t-2xl p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
        <div className="text-center mb-4">
          <span className="text-4xl">🏥</span>
          <h2 className="text-xl font-bold text-slate-800 mt-2">HIPAA Medical Channel</h2>
        </div>
        <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 mb-5 text-base text-pink-800 leading-relaxed">
          <strong>Protected Health Information Notice</strong><br />
          This channel contains Protected Health Information (PHI) under HIPAA. All messages are
          AES-256-GCM encrypted and access is logged for audit compliance. By continuing, you
          acknowledge this channel is for child medical discussions only and all access is recorded
          under 45 CFR §164.312.
        </div>
        <button
          onClick={onAccept}
          className="w-full py-4 rounded-xl bg-pink-600 text-white font-bold text-base mb-3"
        >
          I Understand — Enter Medical Channel
        </button>
        <button onClick={onCancel} className="w-full py-3 text-slate-500 text-base font-medium">
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── New Conversation Modal ────────────────────────────────────────────────────
function NewConvModal({ onClose, onCreate }) {
  const [type, setType]   = useState('coparent')
  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleCreate() {
    setSaving(true)
    setError('')
    try {
      const conv = await onCreate({ type, title: title.trim() || undefined })
      if (conv) onClose()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div className="w-full bg-white rounded-t-2xl p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
        <h2 className="text-lg font-bold text-slate-800 mb-4">New Conversation</h2>

        <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Channel Type</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {Object.entries(TYPE_META).map(([t, m]) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`flex items-center gap-2 p-3.5 rounded-xl border-2 text-base font-medium transition-all ${
                type === t
                  ? `${m.ring} ${m.color}`
                  : 'border-slate-200 text-slate-600'
              }`}
            >
              <span>{m.emoji}</span> {m.label}
            </button>
          ))}
        </div>

        <label className="block text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">
          Title (optional)
        </label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={`e.g. ${
            type === 'medical' ? 'Allergy Discussions' :
            type === 'attorney' ? 'Motion Strategy' :
            type === 'coparent' ? 'General Messages' : 'Support'
          }`}
          className="w-full border border-slate-200 rounded-xl px-4 py-3.5 text-base text-slate-800 outline-none focus:border-brand-500 mb-4"
        />

        {type === 'medical' && (
          <div className="bg-pink-50 border border-pink-200 rounded-xl p-3.5 mb-4 text-sm text-pink-700">
            🏥 This will be a HIPAA-compliant channel with full audit logging.
          </div>
        )}
        {type === 'attorney' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3.5 mb-4 text-sm text-yellow-700">
            ⚖️ Messages in this channel are marked attorney-client privileged.
          </div>
        )}

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <button
          onClick={handleCreate}
          disabled={saving}
          className="w-full py-4 rounded-xl bg-brand-700 text-white font-bold text-base disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create Channel'}
        </button>
      </div>
    </div>
  )
}

// ── Conversation List Item ────────────────────────────────────────────────────
function ConvItem({ conv, onSelect }) {
  const meta = TYPE_META[conv.type] || TYPE_META.admin
  return (
    <button
      onClick={() => onSelect(conv)}
      className="w-full flex items-center gap-3 p-4 bg-white border-b border-slate-100 active:bg-slate-50 text-left"
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-xl flex-shrink-0 ${meta.color}`}>
        {meta.emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-base text-slate-800 truncate">{conv.title}</span>
          {conv.is_hipaa     ? <span className="text-xs font-bold bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-full flex-shrink-0">HIPAA</span> : null}
          {conv.is_privileged? <span className="text-xs font-bold bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full flex-shrink-0">Privileged</span> : null}
        </div>
        <p className="text-sm text-slate-500 truncate mt-0.5">
          {conv.last_body || 'No messages yet'}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-xs text-slate-500">{fmt(conv.last_message_at || conv.updated_at)}</span>
        {conv.unread_count > 0 && (
          <span className="bg-brand-700 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {conv.unread_count > 9 ? '9+' : conv.unread_count}
          </span>
        )}
      </div>
    </button>
  )
}

// ── Message Bubble ────────────────────────────────────────────────────────────
function MsgBubble({ msg, participants = [] }) {
  const [showReceipts, setShowReceipts] = useState(false)
  const readByOthers = (msg.read_by || []).filter(r => r.userId !== msg.sender_id)

  function participantName(userId) {
    const p = participants.find(p => p.id === userId)
    return p?.name || p?.email || 'Co-Parent'
  }

  return (
    <div className={`flex flex-col mb-3 ${msg.is_mine ? 'items-end' : 'items-start'}`}>
      {!msg.is_mine && (
        <span className="text-xs text-slate-500 font-medium mb-1 ml-1">{msg.sender_name}</span>
      )}
      <button
        onClick={() => setShowReceipts(s => !s)}
        className={`max-w-[75%] px-5 py-3 rounded-2xl text-base leading-relaxed text-left ${
          msg.is_mine
            ? 'bg-brand-700 text-white rounded-br-sm'
            : 'bg-slate-100 text-slate-800 rounded-bl-sm'
        }`}
      >
        {msg.text}
      </button>
      <div className="flex items-center gap-1.5 mt-0.5 mx-1">
        <span className="text-xs text-slate-500">{fmtFull(msg.created_at)}</span>
        {msg.is_mine && (
          <span className={`text-xs font-semibold ${readByOthers.length > 0 ? 'text-green-500' : 'text-slate-400'}`}>
            ✓✓
          </span>
        )}
        {!!msg.sms_sent && (
          <span className="text-xs bg-green-100 text-green-700 px-1.5 rounded-full font-medium">SMS</span>
        )}
{/* integrity_hash exists on msg but is intentionally not displayed */}
      </div>
      {showReceipts && (
        <div className="mt-1 mx-1 bg-white border border-slate-100 rounded-xl shadow-sm px-3 py-2 space-y-1">
          {/* Sent timestamp */}
          <p className="text-sm text-slate-500">
            📤 Sent at <strong>{fmtFull(msg.created_at)}</strong>
          </p>
          {/* Read receipts */}
          {readByOthers.length > 0
            ? readByOthers.map((r, i) => (
                <p key={i} className="text-sm text-slate-500">
                  ✓ Read by <strong>{participantName(r.userId)}</strong> at {fmtFull(r.readAt)}
                </p>
              ))
            : msg.is_mine && (
                <p className="text-sm text-slate-500 italic">Not yet read by recipient</p>
              )
          }
          {/* Integrity hash is stored server-side for court verification — not shown to users */}
        </div>
      )}
    </div>
  )
}

// ── Tone Analysis Warning Modal ───────────────────────────────────────────────
const TONE_CONFIG = {
  neutral:     { emoji: '😐', label: 'Neutral',      color: 'text-slate-600',  bg: 'bg-slate-50',   border: 'border-slate-200' },
  cooperative: { emoji: '🤝', label: 'Cooperative',  color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
  hostile:     { emoji: '🚨', label: 'Hostile',       color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-red-200' },
  concerning:  { emoji: '⚠️', label: 'Concerning',    color: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200' },
}

function ToneWarningModal({ analysis, originalText, onSendAnyway, onUseSuggestion, onEditMessage, onClose }) {
  const cfg = TONE_CONFIG[analysis.tone] || TONE_CONFIG.neutral

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div className="w-full bg-white rounded-t-2xl p-5 shadow-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />

        {/* Tone badge */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${cfg.bg}`}>
            {cfg.emoji}
          </div>
          <div>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-wide">Tone Analysis</p>
            <p className={`text-lg font-bold ${cfg.color}`}>{cfg.label} Message Detected</p>
          </div>
        </div>

        {/* Warning */}
        {analysis.warning && (
          <div className={`${cfg.bg} ${cfg.border} border rounded-xl p-4 mb-4`}>
            <p className={`text-base leading-relaxed ${cfg.color}`}>
              <strong>Why this matters in court:</strong> {analysis.warning}
            </p>
          </div>
        )}

        {/* Flagged phrases */}
        {analysis.flags?.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-slate-500 mb-2">Flagged phrases:</p>
            <div className="flex flex-wrap gap-1.5">
              {analysis.flags.map((f, i) => (
                <span key={i} className="text-sm bg-red-100 text-red-700 px-2 py-1 rounded-lg font-medium">"{f}"</span>
              ))}
            </div>
          </div>
        )}

        {/* Suggested rewrite */}
        {analysis.suggestion && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-slate-500 mb-2">Suggested rewrite:</p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-base text-green-800 leading-relaxed">{analysis.suggestion}</p>
            </div>
          </div>
        )}

        {/* Original message preview */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-500 mb-2">Your original message:</p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-sm text-slate-700 leading-relaxed">{originalText}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          {analysis.suggestion && (
            <button
              onClick={onUseSuggestion}
              className="w-full py-3 rounded-xl bg-green-600 text-white font-bold text-sm"
            >
              ✓ Use Suggested Rewrite
            </button>
          )}
          <button
            onClick={onEditMessage}
            className="w-full py-3 rounded-xl bg-brand-700 text-white font-bold text-sm"
          >
            ✏️ Edit My Message
          </button>
          <button
            onClick={onSendAnyway}
            className="w-full py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm"
          >
            Send Original Anyway
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Chat Thread View ──────────────────────────────────────────────────────────
function ThreadView({ conv, onBack }) {
  const [messages, setMessages]   = useState([])
  const [participants, setParticipants] = useState([])
  const [text, setText]           = useState('')
  const [sending, setSending]     = useState(false)
  const [loading, setLoading]     = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [toneWarning, setToneWarning] = useState(null)  // { analysis, originalText }
  const [toneError, setToneError]     = useState(null)  // brief notice when tone API fails
  const bottomRef = useRef(null)

  // ── Scroll to bottom when keyboard opens so latest message stays visible ────
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let showListener
    const attach = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', () => {
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
      })
    }
    attach()
    return () => { showListener?.remove() }
  }, [])

  const load = useCallback(async () => {
    try {
      const d = await api.getConvMessages(conv.id)
      setMessages(d.messages || [])
      setParticipants(d.participants || [])
      // Mark as read every time messages load so read receipts stay current
      api.markConvRead(conv.id).catch(() => {})
    } catch {}
    finally { setLoading(false) }
  }, [conv.id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Poll every 10s while tab is open
  useEffect(() => {
    const id = setInterval(load, 10000)
    return () => clearInterval(id)
  }, [load])

  // Send message directly (bypasses tone check)
  async function sendMessage(msgText) {
    setSending(true)
    try {
      const d = await api.postConvMessage(conv.id, msgText)
      setMessages(prev => [...prev, d.message])
      setText('')
      setToneWarning(null)
    } catch (e) {
      alert(e.message)
    } finally {
      setSending(false)
    }
  }

  // Primary send — runs tone analysis first for co-parent channels
  async function send() {
    const msg = text.trim()
    if (!msg || sending || analyzing) return

    // Lock the button immediately — prevents double-send during async tone analysis
    setSending(true)

    try {
      // Only analyze tone for co-parent channels (not attorney/medical/admin)
      if (conv.type === 'coparent') {
        setAnalyzing(true)
        try {
          const result = await api.analyzeTone(msg)
          const { analysis } = result
          if (analysis.tone === 'hostile' || analysis.tone === 'concerning') {
            // ⚠️ Reset sending BEFORE returning — otherwise the send button locks
            // permanently if the user dismisses the modal without sending.
            setSending(false)
            setToneWarning({ analysis, originalText: msg })
            return
          }
        } catch (e) {
          // Tone analysis failed — show a brief notice and allow message through
          console.warn('[tone] analysis failed:', e?.message)
          setToneError('Tone check unavailable — sending as-is')
          setTimeout(() => setToneError(null), 4000)
        } finally {
          setAnalyzing(false)
        }
      }

      // Post the message (sendMessage manages its own setSending cycle,
      // so reset here first to avoid a no-op double-set)
      setSending(false)
      await sendMessage(msg)
    } catch {
      setSending(false)
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  function openExport() {
    window.open(`/api/messaging/export?conv_id=${conv.id}`, '_blank')
  }

  const meta = TYPE_META[conv.type] || TYPE_META.admin

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader
        showBack
        onBack={onBack}
        title={`${meta.emoji} ${conv.title}`}
        right={
          <div className="flex items-center gap-1.5">
            {!!conv.is_hipaa      && <span className="text-[9px] font-bold bg-white/20 text-white px-1.5 py-0.5 rounded-full">HIPAA</span>}
            {!!conv.is_privileged && <span className="text-[9px] font-bold bg-white/20 text-white px-1.5 py-0.5 rounded-full">Privileged</span>}
            <button
              onClick={openExport}
              className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition-colors"
              title="Export court transcript"
              aria-label="Export conversation transcript"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          </div>
        }
      />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 no-scrollbar">
        {loading && (
          <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-12">
            <span className="text-5xl">{meta.emoji}</span>
            <p className="text-slate-500 text-sm mt-3">No messages yet.<br />Start the conversation.</p>
          </div>
        )}
        {messages.map(m => <MsgBubble key={m.id} msg={m} participants={participants} />)}
        <div ref={bottomRef} />
      </div>

      {/* Tone analysis indicator */}
      {analyzing && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-t border-blue-100 text-sm text-blue-700 font-medium">
          <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Analyzing tone…
        </div>
      )}

      {/* Tone check failure notice */}
      {toneError && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-t border-amber-100 text-sm text-amber-700">
          ⚠️ {toneError}
        </div>
      )}

      {/* Co-parent tone notice */}
      {conv.type === 'coparent' && !analyzing && !toneError && (
        <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-100 flex-shrink-0">
          <p className="text-xs text-slate-400 text-center">🛡️ Messages are tone-checked before sending to help keep communication court-friendly</p>
        </div>
      )}

      {/* Composer */}
      <div className="flex items-end gap-2 px-4 py-3 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900 flex-shrink-0">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          placeholder="Type a message…"
          className="flex-1 border border-slate-200 dark:border-slate-600 rounded-2xl px-4 py-3.5 text-base text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:border-brand-500 dark:focus:border-blue-500 resize-none no-scrollbar"
          style={{ maxHeight: 140, minHeight: 50 }}
          onInput={e => {
            e.target.style.height = 'auto'
            e.target.style.height = e.target.scrollHeight + 'px'
          }}
        />
        <button
          onClick={send}
          disabled={!text.trim() || sending || analyzing}
          className="w-12 h-12 rounded-full bg-brand-700 text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 active:scale-95 transition-transform"
        >
          {sending || analyzing
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
          }
        </button>
      </div>

      {/* Tone warning modal */}
      {toneWarning && (
        <ToneWarningModal
          analysis={toneWarning.analysis}
          originalText={toneWarning.originalText}
          onSendAnyway={() => sendMessage(toneWarning.originalText)}
          onUseSuggestion={() => {
            setText(toneWarning.analysis.suggestion)
            setToneWarning(null)
          }}
          onEditMessage={() => setToneWarning(null)}
          onClose={() => setToneWarning(null)}
        />
      )}
    </div>
  )
}

// ── Main Screen ───────────────────────────────────────────────────────────────
export default function DirectMessages() {
  const navigate = useNavigate()
  const { setDirectMsgCount } = useApp()
  const [convs, setConvs]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [selected, setSelected]     = useState(null)
  const [showNew, setShowNew]       = useState(false)
  const [hipaaTarget, setHipaaTarget] = useState(null) // conv pending hipaa consent
  const [filter, setFilter]         = useState('all')

  // ── Encryption status (validated live on every open) ─────────────────────
  // null = checking, { active, keyPresent, keyValid, testPassed, error? } = result
  const [encStatus, setEncStatus]   = useState(null)

  async function loadConvs() {
    try {
      const d = await api.getConversations()
      const convList = d.conversations || []
      setConvs(convList)
      // Sync global unread count whenever we reload the list
      const total = convList.reduce((s, c) => s + (c.unread_count || 0), 0)
      setDirectMsgCount(total)
    } catch {}
    finally { setLoading(false) }
  }

  async function checkEncryption() {
    try {
      const status = await api.getEncryptionStatus()
      setEncStatus(status)
    } catch {
      // Network/auth failure — treat as unknown (don't falsely claim encrypted)
      setEncStatus({ active: false, keyPresent: false, keyValid: false, testPassed: false,
        error: 'Could not verify encryption status' })
    }
  }

  useEffect(() => {
    loadConvs()
    checkEncryption()
  }, [])

  function openConv(conv) {
    // Optimistically zero out this conv's unread in the list + global count
    if (conv.unread_count > 0) {
      setConvs(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c))
      setDirectMsgCount(prev => Math.max(0, prev - conv.unread_count))
      api.markConvRead(conv.id).catch(() => {})
    }
    setSelected(conv)
  }

  function handleSelect(conv) {
    if (conv.is_hipaa && !sessionStorage.getItem(`hipaa_ok_${conv.id}`)) {
      setHipaaTarget(conv)
      return
    }
    openConv(conv)
  }

  function hipaaAccept() {
    sessionStorage.setItem(`hipaa_ok_${hipaaTarget.id}`, '1')
    const conv = hipaaTarget
    setHipaaTarget(null)
    openConv(conv)
  }

  async function handleCreate(body) {
    const d = await api.postConversation(body)
    await loadConvs()
    return d.conversation
  }

  const filtered = filter === 'all'
    ? convs
    : convs.filter(c => c.type === filter)

  // ── Thread view ──────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className="flex flex-col h-full bg-white">
        <ThreadView conv={selected} onBack={() => { setSelected(null); loadConvs() }} />
      </div>
    )
  }

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Messages" showBack onBack={() => navigate('/dashboard')} />

      {/* Filter tabs */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto no-scrollbar flex-shrink-0 border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-900">
        {[
          { id: 'all',       label: 'All' },
          { id: 'coparent',  label: '👨‍👩‍👧 Co-Parent' },
          { id: 'attorney',  label: '⚖️ Attorney' },
          { id: 'medical',   label: '🏥 Medical' },
          { id: 'admin',     label: '🔔 Admin' },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === f.id
                ? 'bg-brand-700 text-white dark:bg-blue-600'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Encryption status — validated live on every open */}
      {encStatus === null ? (
        // Still checking — show a neutral "verifying" indicator
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <span className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-500 text-xs font-semibold">Verifying encryption…</span>
        </div>
      ) : encStatus.active ? (
        // Key present, valid, and round-trip test passed ✓
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 flex-shrink-0">
          <span className="text-green-600 dark:text-green-400 text-xs font-semibold">🔒 AES-256-GCM Encrypted · Court-Admissible</span>
        </div>
      ) : (
        // Encryption inactive or broken — prominent warning
        <div className="flex items-start gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-900/25 border-b border-red-200 dark:border-red-800 flex-shrink-0">
          <span className="text-red-500 flex-shrink-0 mt-0.5">⚠️</span>
          <div>
            <p className="text-red-700 dark:text-red-400 text-xs font-bold">Encryption Not Active</p>
            <p className="text-red-600 dark:text-red-400 text-xs mt-0.5 leading-snug">
              {encStatus.error || 'Messages may be stored unencrypted. Contact your administrator.'}
            </p>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        {loading && (
          <div className="text-center py-16 text-slate-500 text-sm">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 px-6">
            <span className="text-5xl">💬</span>
            <p className="text-slate-600 font-semibold mt-4">No conversations yet</p>
            <p className="text-slate-500 text-sm mt-1">
              {filter !== 'all'
                ? `No ${TYPE_META[filter]?.label || filter} channels found.`
                : 'Start a secure channel with your co-parent, attorney, or for medical discussions.'}
            </p>
            <button
              onClick={() => setShowNew(true)}
              className="mt-5 px-6 py-2.5 rounded-full bg-brand-700 text-white text-sm font-bold"
            >
              + New Channel
            </button>
          </div>
        )}
        {filtered.map(c => (
          <ConvItem key={c.id} conv={c} onSelect={handleSelect} />
        ))}
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowNew(true)}
        className="fixed bottom-20 right-5 w-14 h-14 rounded-full bg-brand-700 text-white shadow-xl flex items-center justify-center text-2xl font-bold z-30 active:scale-95 transition-transform"
      >
        +
      </button>

      {/* Modals */}
      {hipaaTarget && (
        <HipaaModal
          onAccept={hipaaAccept}
          onCancel={() => setHipaaTarget(null)}
        />
      )}
      {showNew && (
        <NewConvModal
          onClose={() => setShowNew(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  )
}
